/**
 * 時程日曆「RD」粒度的彙總。
 *
 * 這一頁的檢視粒度有三層，回答的是三個不同的問題：
 *   專案 —— 現在有哪些案子在跑、走到哪（老闆視角）
 *   工項 —— 這一天有哪幾件事要開始／截止（執行視角）
 *   RD   —— 這一天誰做了幾件工項（日報視角）
 *
 * 第三層的用途是「工項＝每日日報」：一眼看出某天有沒有人回報、誰沒回報。
 * 所以空白的那幾天才是重點，不能只把有資料的日子畫出來就算。
 *
 * 為什麼是純函式、放在獨立檔案：
 *   彙總本身不依賴 React、不依賴 API，輸入輸出都是普通陣列，
 *   可以單獨驗證；Calendar.js 已經兩千多行，再往裡面堆只會更難改。
 *
 * 為什麼刻意不篩「只有 RD 角色的人」：
 *   前端拿不到別人的角色。要篩就得打 /Auth/Admin/List，而那一支一般 PM 會 403
 *   （見 Calendar.js 指派人下拉的說明）—— 為了篩掉幾個人而讓整頁在部分角色底下
 *   變成空白並不划算。實務上會被指派工項的就是 RD，列出所有指派人即可。
 */

import { MdCheck, MdCircle, MdErrorOutline, MdRemove, MdWarningAmber } from "react-icons/md";

/**
 * 一天內同一個人只產生一列。
 *
 * 計數用「工項開始」那一則（kind === "start"），不是「截止」：
 * 這一層要回答的是「那天做了什麼」，截止日是承諾、不是當天的產出，
 * 而且同一件工項的起訖若都算，跨日工項會被重複計。
 * 這個口徑與後端「漏填工項日期偵測」一致（PerformanceService 用 start_date
 * 判斷某個上班日有沒有工項），兩邊才不會各說各話。
 *
 * @param {Array} occurrences 工項粒度的事件（Calendar.js 的 occurrences）
 * @param {Array<string>} palette 取色盤，依人員索引循環取色
 * @returns {Array} 與工項事件同形狀的列，可直接餵給既有的 byDay / 月週議程 / 當日視窗
 */
export function buildRdRows(occurrences, palette) {
  const pal = Array.isArray(palette) && palette.length ? palette : ["#8a94a3"];

  /* 先把人員排出穩定的順序再配色：用出現順序配色的話，
     換一個月、或篩掉某個專案，同一個人的顏色就會跳掉。 */
  const people = [];
  const seen = new Set();
  (occurrences || []).forEach((o) => {
    if (!o || o.kind !== "start") return;
    const key = o.assigneeKey || "__none__";
    if (seen.has(key)) return;
    seen.add(key);
    people.push({ key, name: o.assignee || "未指派" });
  });
  people.sort((a, b) => String(a.name).localeCompare(String(b.name), "zh-Hant"));

  const colorOf = {};
  people.forEach((p, i) => {
    colorOf[p.key] = pal[i % pal.length];
  });

  /* (日期, 人) → 件數。用 Map 保留插入順序，最後再依人名排序輸出，
     同一天的人員順序才不會每次 render 都不一樣。 */
  const bucket = new Map();
  (occurrences || []).forEach((o) => {
    if (!o || o.kind !== "start" || !o.ymd) return;
    const who = o.assigneeKey || "__none__";
    const id = `${o.ymd}|${who}`;
    /* 「做完沒」看工項狀態：已完成／已關閉才算結案，其餘（待處理／進行中／審核中）都還沒 */
    const unfinished = o.status !== "done" && o.status !== "closed";
    const hit = bucket.get(id);
    if (hit) {
      hit.count += 1;
      if (unfinished) hit.open += 1;
      /* 當日視窗要列出實際做了哪幾件，不然點開只看得到一個數字 */
      hit.items.push(o.name || o.title || "");
      return;
    }
    bucket.set(id, {
      ymd: o.ymd,
      who,
      name: o.assignee || "未指派",
      count: 1,
      open: unfinished ? 1 : 0,
      items: [o.name || o.title || ""],
    });
  });

  return [...bucket.values()]
    .sort((a, b) => a.ymd.localeCompare(b.ymd) || String(a.name).localeCompare(String(b.name), "zh-Hant"))
    .map((r) => ({
      id: `rd-${r.ymd}-${r.who}`,
      kind: "start",
      nature: "當日工項",
      ymd: r.ymd,
      time: "",
      /*
       * 小標直接寫「完成 A / 未完成 B」。
       *
       * 只寫「15 件」看不出那天做完沒 —— 15 件全做完和 15 件全卡著，
       * 在日報視角是完全相反的兩件事，卻會顯示成一模一樣的字。
       * 專案粒度用同一種寫法，同一個畫面不要讓人學兩套講法。
       * 月格一格只有幾十 px，所以數字直接寫在小標上，不要求使用者點開才知道。
       */
      title: `${r.name} · 完成 ${r.count - r.open} / 未完成 ${r.open}`,
      name: `${r.name} · 完成 ${r.count - r.open} / 未完成 ${r.open}`,
      color: colorOf[r.who] || pal[0],
      /* 不歸屬任何專案：RD 粒度是跨專案看人，專案膠囊不該把它篩掉 */
      projKey: "",
      projectId: null,
      projectName: "",
      phaseName: "",
      assigneeKey: r.who === "__none__" ? "" : r.who,
      assignee: r.name,
      status: "",
      prio: "",
      code: "",
      issueType: "",
      /* 當日視窗的說明列出實際的工項標題，最多五筆，其餘用「…等 N 件」收掉 */
      desc:
        r.items.length > 5
          ? `${r.items.slice(0, 5).join("、")}…等 ${r.items.length} 件`
          : r.items.join("、"),
      progress: null,
      startRaw: r.ymd,
      endRaw: r.ymd,
      overdue: false,
      count: r.count,
      openCount: r.open,
    }));
}

/**
 * 區間內「一個人都沒有回報」的上班日。
 *
 * 這是 RD 粒度真正要凸顯的東西：有資料的日子看得到，沒資料的日子才是問題。
 * 週六日直接排除；國定假日由呼叫端把 holidayMap 帶進來（日曆頁本來就有）。
 *
 * @param {Array} rows buildRdRows 的輸出
 * @param {string} startYmd 區間起（含）
 * @param {string} endYmd 區間迄（含）
 * @param {Object} holidayMap { "2026-09-28": "教師節" }
 * @returns {Array<string>} 沒有任何工項的上班日
 */
export function idleWorkdays(rows, startYmd, endYmd, holidayMap) {
  if (!startYmd || !endYmd || startYmd > endYmd) return [];
  const has = new Set((rows || []).map((r) => r.ymd));
  const holidays = holidayMap || {};
  const out = [];

  const [sy, sm, sd] = startYmd.split("-").map(Number);
  const [ey, em, ed] = endYmd.split("-").map(Number);
  const cur = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);

  /* 用本地時間逐日推進，不用 UTC：台灣是 UTC+8，改用 toISOString 取日期的話
     凌晨 0~8 點會整段位移一天。 */
  while (cur <= end) {
    const ymd = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(
      cur.getDate()
    ).padStart(2, "0")}`;
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6 && !holidays[ymd] && !has.has(ymd)) out.push(ymd);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/* ================================================================
   里程碑時程（/Schedule，設計稿 #screen-schedule L1402-1707）

   為什麼也放在這個檔案：
   這一頁的「哪個里程碑會超過、還剩幾天緩衝、長條要從第幾欄畫到第幾欄」
   全是純算術，跟 React 無關。放在頁面裡的話，要驗證「9/29 開始、10/1 截止
   的工項在 8 欄的軸上是不是第 3 欄起算」就得先把整頁 render 出來。
   上面的 buildRdRows / idleWorkdays 是同一個理由；那兩支的行為一律不動，
   這一段只新增匯出函式。

   為什麼輸入輸出都是 "YYYY-MM-DD" 字串而不是 dayjs 物件：
   API 回的是帶時區的 DateTimeOffset，「算哪一天」這件事必須在呼叫端用 dayjs
   照當地時區切好再進來（專案慣例：日期一律 dayjs）。這裡只做純日期算術，
   維持這個檔案零相依、可以直接用 node 跑。
   ================================================================ */

/**
 * 提醒緩衝天數的「讀不到設定時」退路。
 *
 * 真正在用的門檻已經存在後端 system_setting 的 goal.buffer_days（「設定 → 偏好設定」
 * 可以改，讀取端點 GET /Setting/Goal），頁面一律透過 useGoalBufferDays 拿了再傳進來；
 * 只有在讀不到（離線、端點還沒佈上去）時才會退回這個數字。
 *
 * 3 這個數字有兩份：這裡與後端 /Setting/Goal 沒設定過時回的預設值 ——
 * 同一條規則前後端各實作一次，只改一邊就會變成「畫面說紅燈、信卻沒來」，
 * 兩邊都沒人信。要改就兩邊一起改。（設計稿 L1430 也是 3 天。）
 */
export const DEFAULT_BUFFER_DAYS = 3;

/**
 * 里程碑「完成」的定義，畫面上要講的那一句。
 * 時程頁的里程碑分頁與專案詳情的里程碑分頁共用這一句 —— 兩邊講法不同的話，
 * 使用者會以為兩邊算法不同。規則本身在下面 buildScheduleView 的 level（done / empty）。
 */
export const GOAL_DONE_RULE = "里程碑底下的工項全部完成即視為達成；尚未排入工項的里程碑不會判定完成或逾期。";

/** "YYYY-MM-DD" → UTC 毫秒。純日期算術一律走 UTC，跨夏令時間才不會多一天少一天 */
function ymdMs(ymd) {
  if (!ymd || typeof ymd !== "string") return NaN;
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return NaN;
  return Date.UTC(y, m - 1, d);
}

function msYmd(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate()
  ).padStart(2, "0")}`;
}

/** b - a，單位是天。任一邊不是合法日期就回 null，呼叫端才不會拿 NaN 去算天數 */
export function ymdDiff(a, b) {
  const x = ymdMs(a);
  const y = ymdMs(b);
  if (Number.isNaN(x) || Number.isNaN(y)) return null;
  return Math.round((y - x) / 86400000);
}

function shiftYmd(ymd, days) {
  const ms = ymdMs(ymd);
  if (Number.isNaN(ms)) return "";
  return msYmd(ms + days * 86400000);
}

/**
 * 該日所屬那一週的星期一。
 *
 * 刻度切在星期一而不是「第一件工項的那一天」：軸上一欄＝一週，
 * 起算日隨資料浮動的話，同一個專案多排一件工項就整排刻度位移，
 * 使用者記不住「10/6 那一欄」在哪裡。
 */
function weekStart(ymd) {
  const ms = ymdMs(ymd);
  if (Number.isNaN(ms)) return "";
  const dow = new Date(ms).getUTCDay(); // 0=星期日
  const back = dow === 0 ? 6 : dow - 1;
  return msYmd(ms - back * 86400000);
}

function minYmd(list) {
  return list.filter(Boolean).sort()[0] || "";
}
function maxYmd(list) {
  const s = list.filter(Boolean).sort();
  return s[s.length - 1] || "";
}

/**
 * (a, b] 之間還有幾個上班日。
 *
 * 「只剩 2 天緩衝」如果那兩天是週六日，實際上一天都沒有 —— 這正是最會出事的情形，
 * 所以緩衝除了日曆天之外還要算上班天。週六日一定排除；國定假日看呼叫端有沒有把
 * holidayMap 帶進來（/Holiday/List 還沒回來時就只扣週末，仍然是誠實的下限）。
 *
 * 用 (a, b]：a 是最後一件工項的截止日，那天已經被工項用掉了，不算緩衝。
 */
export function workdaysBetween(aYmd, bYmd, holidayMap) {
  const from = ymdMs(aYmd);
  const to = ymdMs(bYmd);
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return 0;
  const days = holidayMap || {};
  let n = 0;
  for (let ms = from + 86400000; ms <= to; ms += 86400000) {
    const dow = new Date(ms).getUTCDay();
    if (dow !== 0 && dow !== 6 && !days[msYmd(ms)]) n += 1;
  }
  return n;
}

/** "2026-10-15" → "10/15"。長條與刻度只放得下短日期，句子裡的長日期由畫面自己格式化 */
function mdOf(ymd) {
  if (!ymd) return "";
  const [, m, d] = ymd.split("-");
  return `${Number(m)}/${Number(d)}`;
}

/** 已結案的工項不再參與「會不會拖垮里程碑」的計算 */
function isClosedTask(t) {
  return t.statusKey === "done" || t.statusKey === "closed";
}

/**
 * 長條的顏色與條內那一句話。
 *
 * 判斷順序就是輕重順序：一條只能講一件事，先講最會出事的那一件。
 * 「已逾期」與「超過里程碑日期」都是紅的，但要說的不一樣 ——
 * 前者是這件工項自己遲了，後者是它遲不遲無所謂、但會把里程碑拖垮。
 *
 * 逾期一律用日界比較（ymdDiff < 0），不是「截止時刻 < 現在」：
 * due_date 存的是當天 00:00，用時刻比的話今天到期的工項早上八點就被判逾期。
 */
function barState(task, goal, todayYmd, bufferDays) {
  if (isClosedTask(task)) return { level: "done", mark: <MdCheck />, text: "已完成" };

  const dDue = task.dueYmd ? ymdDiff(todayYmd, task.dueYmd) : null;
  if (dDue !== null && dDue < 0) return { level: "attention", mark: <MdWarningAmber />, text: `逾期 ${-dDue} 天` };

  /* 正數＝這件工項比里程碑日期還晚幾天（會拖垮里程碑）；負數的絕對值＝離里程碑還有幾天緩衝 */
  const over = goal && goal.endYmd && task.dueYmd ? ymdDiff(goal.endYmd, task.dueYmd) : null;
  if (over !== null && over > 0) {
    return { level: "attention", mark: <MdWarningAmber />, text: `超過 ${mdOf(goal.endYmd)} ${over} 天` };
  }
  if (dDue === 0) return { level: "attention", mark: <MdWarningAmber />, text: "今日到期" };

  /* 沒有人負責的工項不算風險，但也不能算「照計畫」—— 照計畫是有人在做才成立的 */
  if (!task.assignee) return { level: "closed", mark: <MdRemove />, text: "未指派" };

  if (over !== null && -over < bufferDays) {
    return { level: "warn", mark: <MdErrorOutline />, text: `緩衝剩 ${-over} 天` };
  }
  return { level: "plan", mark: <MdCircle size="0.6em" />, text: "進度正常" };
}

/**
 * 里程碑頁的全部衍生資料：軸的欄位、每個里程碑的燈號、每件工項的長條、兩條風險橫幅。
 *
 * 一次算完而不是畫面各段各算一次：橫幅、里程碑卡、橫向軸、直向階段清單是
 * 同一件事的四種寫法，分開算遲早會出現「橫幅說超過 4 天、卡片說還有緩衝」。
 *
 * @param {Object} input
 * @param {Array}  input.phases 里程碑（呼叫端已正規化）：{ id, name, desc, endYmd, createdYmd, statusCode }
 * @param {Array}  input.tasks  工項（呼叫端已正規化）：{ id, title, assignee, startYmd, dueYmd, statusKey, phaseId }
 * @param {string} input.todayYmd 今天（當地時區日界，呼叫端用 dayjs 切好）
 * @param {number} input.bufferDays 提醒門檻，預設 DEFAULT_BUFFER_DAYS
 * @param {Object} input.holidayMap { "2026-10-10": "國慶日" }，沒帶就只扣週末
 * @returns {Object} { goals, bars, unplanned, weeks, cols, rangeStart, rangeEnd, bufferDays, alerts, counts }
 */
export function buildScheduleView(input) {
  const {
    phases = [],
    tasks = [],
    todayYmd = "",
    bufferDays = DEFAULT_BUFFER_DAYS,
    holidayMap = {},
  } = input || {};

  const planned = [];
  const unplanned = [];
  (Array.isArray(tasks) ? tasks : []).forEach((t) => {
    if (!t) return;
    /* phase_id 可能是 null、0 或空字串（後端投影是可空的 int），三種都算「沒排進任何里程碑」 */
    const pid = t.phaseId;
    if (pid === null || pid === undefined || pid === "" || Number(pid) === 0) unplanned.push(t);
    else planned.push(t);
  });

  const byPhase = new Map();
  planned.forEach((t) => {
    const k = String(t.phaseId);
    if (!byPhase.has(k)) byPhase.set(k, []);
    byPhase.get(k).push(t);
  });

  /* ---------- 每個里程碑的燈號 ---------- */
  const goals = (Array.isArray(phases) ? phases : []).filter(Boolean).map((p) => {
    const own = (byPhase.get(String(p.id)) || []).slice().sort((a, b) => {
      const av = a.startYmd || a.dueYmd || "";
      const bv = b.startYmd || b.dueYmd || "";
      return av.localeCompare(bv);
    });

    /* 「工項做完預計哪一天」＝ 底下最晚的截止日。沒有任何工項排過日期時是 null，
       這時候緩衝天數算不出來，不能拿 0 當成「剛好沒緩衝」。 */
    const lastEndYmd = maxYmd(own.map((t) => t.dueYmd)) || null;
    const slack = lastEndYmd && p.endYmd ? ymdDiff(lastEndYmd, p.endYmd) : null; // 正數＝還有緩衝
    const overrunDays = slack !== null && slack < 0 ? -slack : 0;
    const bufferLeft = slack !== null && slack >= 0 ? slack : null;
    /* 同一段緩衝的上班天。日曆天用來講那句「只剩 2 天」（跟設計稿一致），
       上班天用來補一句「扣掉假日其實只有 0 天」，兩個數字不互相取代 */
    const bufferWorkdays =
      bufferLeft === null ? null : workdaysBetween(lastEndYmd, p.endYmd, holidayMap);
    const daysLeft = p.endYmd ? ymdDiff(todayYmd, p.endYmd) : null;

    const allDone = own.length > 0 && own.every(isClosedTask);
    const counts = {
      total: own.length,
      done: own.filter(isClosedTask).length,
      unassigned: own.filter((t) => !t.assignee && !isClosedTask(t)).length,
      undated: own.filter((t) => !t.dueYmd && !t.startYmd).length,
      dueToday: own.filter((t) => !isClosedTask(t) && t.dueYmd && ymdDiff(todayYmd, t.dueYmd) === 0)
        .length,
      overdue: own.filter((t) => !isClosedTask(t) && t.dueYmd && ymdDiff(todayYmd, t.dueYmd) < 0)
        .length,
      /* 排到里程碑之後：截止日比里程碑日期晚（ymdDiff 回的是「晚幾天」，正數才是超過） */
      overrun: p.endYmd
        ? own.filter((t) => !isClosedTask(t) && t.dueYmd && ymdDiff(p.endYmd, t.dueYmd) > 0).length
        : 0,
    };

    /* 拖垮里程碑的就是最晚那一件，橫幅要點名它 —— 只寫「有一件會超過」的話，
       看的人還是得自己回清單一件一件找 */
    const worstTask = overrunDays > 0 ? own.find((t) => t.dueYmd === lastEndYmd) || null : null;

    let level = "plan";
    if (p.statusCode === 2 || allDone) level = "done";
    /* 底下沒有任何工項：里程碑的完成與否完全由工項推導（後端 PutPhase 不接受手動改狀態），
       0 件工項時算不出「完成」也算不出「來不及」。過了里程碑日期也不能報成逾期 ——
       否則會出現「尚有 0 件未完成」這種自相矛盾的警告。單獨標成 empty，不進風險橫幅。 */
    else if (own.length === 0) level = "empty";
    else if (overrunDays > 0) level = "attention";
    else if (daysLeft !== null && daysLeft < 0) level = "attention";
    else if (bufferLeft !== null && bufferLeft < bufferDays) level = "warn";

    return {
      ...p,
      level,
      tasks: own,
      lastEndYmd,
      overrunDays,
      bufferLeft,
      bufferWorkdays,
      daysLeft,
      counts,
      worstTask,
    };
  });

  /* ---------- 軸的範圍與刻度 ---------- */
  const stamps = [todayYmd]
    .concat(goals.map((g) => g.endYmd))
    .concat(planned.map((t) => t.startYmd))
    .concat(planned.map((t) => t.dueYmd))
    .filter(Boolean);

  const rangeStart = stamps.length ? weekStart(minYmd(stamps)) : "";
  const last = stamps.length ? maxYmd(stamps) : "";
  /* 欄數由資料決定，不寫死設計稿的 8。專案跨半年就是二十幾欄 ——
     那種情況橫向軸本來就難讀，直向階段清單才是答案，所以不另外截斷資料。 */
  const spanDays = rangeStart && last ? ymdDiff(rangeStart, last) : null;
  const cols = spanDays === null ? 0 : Math.max(1, Math.ceil((spanDays + 1) / 7));
  const totalDays = cols * 7;
  const rangeEnd = cols ? shiftYmd(rangeStart, totalDays - 1) : "";

  const weeks = [];
  for (let i = 0; i < cols; i += 1) {
    const ymd = shiftYmd(rangeStart, i * 7);
    weeks.push({ ymd, label: mdOf(ymd) });
  }

  /** 日期在軸上的比例（0~1）。取當天的中點，旗標才會落在那一格中間而不是壓在左緣 */
  const ratioOf = (ymd) => {
    if (!cols || !ymd) return null;
    const d = ymdDiff(rangeStart, ymd);
    if (d === null) return null;
    return Math.min(1, Math.max(0, (d + 0.5) / totalDays));
  };

  goals.forEach((g) => {
    g.ratio = ratioOf(g.endYmd);
    /* 靠右緣的旗標把標籤翻到線的左邊，否則 220px 寬的標籤會被擠成兩三行 */
    g.flip = g.ratio !== null && g.ratio > 0.8;
  });

  /* ---------- 長條 ---------- */
  const bars = [];
  goals.forEach((g) => {
    g.tasks.forEach((t) => {
      const s = t.startYmd || t.dueYmd;
      const e = t.dueYmd || t.startYmd;
      const st = barState(t, g, todayYmd, bufferDays);
      /* 兩個日期都沒有的工項畫不出長條。不要拿「今天」硬塞一格 ——
         那會變成一條看起來排好了、其實沒有人排過的工項。 */
      if (!cols || !s || !e) {
        bars.push({ ...t, goalId: g.id, goalName: g.name, col: null, span: 0, ...st });
        return;
      }
      const sd = Math.min(Math.max(ymdDiff(rangeStart, s), 0), totalDays - 1);
      const ed = Math.min(Math.max(ymdDiff(rangeStart, e), sd), totalDays - 1);
      const sCol = Math.floor(sd / 7);
      const eCol = Math.floor(ed / 7);
      bars.push({
        ...t,
        goalId: g.id,
        goalName: g.name,
        /* 格線第一欄是左側的工項名稱欄（Goals.js 的 TRACK_ROW_SX），所以週次要 +2 */
        col: sCol + 2,
        span: Math.max(1, eCol - sCol + 1),
        ...st,
      });
    });
  });

  /* ---------- 兩條風險橫幅 ---------- */
  /* 只有真的算出該狀態才回物件；沒事就是 null，畫面整條不渲染（硬規則 2） */
  const overrun = goals.filter((g) => g.level === "attention" && g.overrunDays > 0);
  const overdue = goals.filter(
    (g) => g.level === "attention" && g.overrunDays === 0 && g.daysLeft !== null && g.daysLeft < 0
  );
  let attention = null;
  if (overrun.length) {
    const g = overrun.slice().sort((a, b) => b.overrunDays - a.overrunDays)[0];
    attention = { kind: "overrun", goal: g, task: g.worstTask, days: g.overrunDays, others: overrun.length - 1 };
  } else if (overdue.length) {
    const g = overdue.slice().sort((a, b) => a.daysLeft - b.daysLeft)[0];
    attention = { kind: "overdue", goal: g, task: null, days: -g.daysLeft, others: overdue.length - 1 };
  }

  const tight = goals.filter((g) => g.level === "warn");
  const warn = tight.length
    ? (() => {
        const g = tight.slice().sort((a, b) => a.bufferLeft - b.bufferLeft)[0];
        return {
          kind: "tight",
          goal: g,
          days: g.bufferLeft,
          workdays: g.bufferWorkdays,
          others: tight.length - 1,
        };
      })()
    : null;

  return {
    goals,
    bars,
    unplanned,
    weeks,
    cols,
    rangeStart,
    rangeEnd,
    bufferDays,
    alerts: { attention, warn },
    counts: { goals: goals.length, planned: planned.length, unplanned: unplanned.length },
  };
}
